/**
 * Project-type vocabulary — single source of truth for every project-type dropdown and display.
 *
 * `project_type` is free text on the server. New projects store the compact code (`AppDev`, …);
 * older rows may hold the spaced label (`App Dev`). `projectTypeLabel()` renders both the same way.
 */
export const PROJECT_TYPE_OPTIONS = [
  { value: "AppDev", label: "App Dev" },
  { value: "AppMod", label: "App Mod" },
  { value: "AIML", label: "AI/ML" },
  { value: "Migration", label: "Migration" },
  { value: "5x5x5", label: "5x5x5" },
] as const;

export type ProjectTypeCode = (typeof PROJECT_TYPE_OPTIONS)[number]["value"];

/** Lower-cased, separator-free key: "App Dev" / "app-dev" / "AppDev" → "appdev"; "AI/ML" → "aiml". */
function normaliseKey(value: string): string {
  return value.toLowerCase().replace(/[\s/_-]+/g, "");
}

const LABEL_BY_KEY: ReadonlyMap<string, string> = new Map(
  PROJECT_TYPE_OPTIONS.flatMap((o) => [
    [normaliseKey(o.value), o.label],
    [normaliseKey(o.label), o.label],
  ]),
);

/**
 * Display label for a stored project type. Accepts codes ("AppDev") and legacy spaced strings
 * ("App Dev"); unknown values are returned as-is (trimmed), empty → "—".
 */
export function projectTypeLabel(code: string | null | undefined): string {
  const raw = (code ?? "").trim();
  if (!raw) return "—";
  return LABEL_BY_KEY.get(normaliseKey(raw)) ?? raw;
}
